import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { templatesApi } from "@/api/careplans";
import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { FullPageSpinner } from "@/components/FullPageSpinner";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { TaskChecklistEditor } from "./TaskChecklistEditor";

const CATEGORY_OPTIONS = [
  { value: "personal_care", label: "Personal Care" },
  { value: "medication", label: "Medication" },
  { value: "mobility", label: "Mobility" },
  { value: "companionship", label: "Companionship" },
  { value: "nursing", label: "Nursing" },
  { value: "custom", label: "Custom" },
];

const BLANK_BASELINE = {
  name: "",
  description: "",
  category: "personal_care",
  defaultDuration: 60,
  isActive: true,
  tasks: [],
};

function snapshotFromData(data) {
  return {
    name: data.name || "",
    description: data.description || "",
    category: data.category || "personal_care",
    defaultDuration: data.default_duration_minutes || 60,
    isActive: data.is_active ?? true,
    tasks: data.tasks || [],
  };
}

export function TemplateFormPage() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("personal_care");
  const [defaultDuration, setDefaultDuration] = useState(60);
  const [isActive, setIsActive] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [baseline, setBaseline] = useState(BLANK_BASELINE);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditing) {
      templatesApi
        .get(id)
        .then((data) => {
          const snap = snapshotFromData(data);
          setName(snap.name);
          setDescription(snap.description);
          setCategory(snap.category);
          setDefaultDuration(snap.defaultDuration);
          setIsActive(snap.isActive);
          setTasks(snap.tasks);
          setBaseline(snap);
        })
        .catch((err) => setError(err.message || "Failed to load template."))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const isDirty = useMemo(() => {
    if (
      name !== baseline.name ||
      description !== baseline.description ||
      category !== baseline.category ||
      Number(defaultDuration) !== Number(baseline.defaultDuration) ||
      isActive !== baseline.isActive
    ) {
      return true;
    }
    return JSON.stringify(tasks) !== JSON.stringify(baseline.tasks);
  }, [name, description, category, defaultDuration, isActive, tasks, baseline]);

  const saveDisabled = saving || (isEditing && !isDirty);

  const discard = () => {
    setName(baseline.name);
    setDescription(baseline.description);
    setCategory(baseline.category);
    setDefaultDuration(baseline.defaultDuration);
    setIsActive(baseline.isActive);
    setTasks(baseline.tasks);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing && !isDirty) return;
    setError(null);
    setSaving(true);

    const payload = {
      name,
      description,
      category,
      default_duration_minutes: Number(defaultDuration),
      is_active: isActive,
      tasks,
    };

    try {
      if (isEditing) {
        await templatesApi.update(id, payload);
      } else {
        await templatesApi.create(payload);
      }
      navigate("/care-plans/templates");
    } catch (err) {
      setError(err.detail || err.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Archive this care plan template?")) return;
    try {
      await templatesApi.delete(id);
      navigate("/care-plans/templates");
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <FullPageSpinner label="Loading template" />;
  }

  return (
    <PageShell>
      <PageHeader
        title={isEditing ? "Edit Care Plan Template" : "New Care Plan Template"}
        description="Configure default tasks and service parameters for this template."
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/care-plans/templates">← Back to Templates</Link>
          </Button>
        }
      />

      <PageBody>
        <Alert>{error}</Alert>

        <form onSubmit={handleSubmit} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">General Details</CardTitle>
              <CardDescription>
                Name, category, duration, and whether this template is available for assignment.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Template Name" name="name" className="sm:col-span-2">
                  {(props) => (
                    <Input
                      {...props}
                      type="text"
                      placeholder="e.g. Standard Personal Hygiene & Wellness"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  )}
                </Field>

                <Field label="Category" name="category">
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={category}
                      onValueChange={setCategory}
                      options={CATEGORY_OPTIONS}
                      placeholder="Select category…"
                    />
                  )}
                </Field>

                <Field label="Default Duration (minutes)" name="default_duration">
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={15}
                      step={15}
                      value={defaultDuration}
                      onChange={(e) => setDefaultDuration(e.target.value)}
                    />
                  )}
                </Field>

                <Field label="Description (Optional)" name="description" className="sm:col-span-2">
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={3}
                      placeholder="Overview of care goals and clinical notes for this template..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                    />
                  )}
                </Field>
              </div>

              <div className="flex items-center justify-between gap-4 border-t border-dashed border-border pt-4">
                <div className="space-y-1">
                  <div className="text-sm font-medium">Active Template</div>
                  <p className="text-sm text-muted-foreground">
                    Make this template available for client assignment immediately.
                  </p>
                </div>
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  aria-label="Active template"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Template Task Checklist</CardTitle>
              <CardDescription>
                Define the standard tasks caregivers need to complete when this plan is assigned.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TaskChecklistEditor tasks={tasks} onChange={setTasks} />
            </CardContent>
            <CardFooter className="flex flex-wrap items-center gap-2 border-t border-border px-4 pt-4">
              <Button type="submit" disabled={saveDisabled}>
                {saving ? "Saving..." : isEditing ? "Update Template" : "Create Template"}
              </Button>
              {isEditing ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!isDirty || saving}
                  onClick={discard}
                >
                  Discard
                </Button>
              ) : null}
              <Button asChild type="button" variant="ghost">
                <Link to="/care-plans/templates">Cancel</Link>
              </Button>
              {isEditing ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="ml-auto"
                  onClick={handleDelete}
                >
                  Delete Template
                </Button>
              ) : null}
            </CardFooter>
          </Card>
        </form>
      </PageBody>
    </PageShell>
  );
}
